/**
 * Open time slot (operating hours) experiences sell each conceptual ticket once
 * per offered duration — "Adult 1 hour" and "Adult 2 hours" are separate
 * variants linked by a shared `groupKey` and distinguished by
 * `durationSeconds`. These helpers implement the storefront-side rules for
 * that model: show only the selected duration's variants, and carry a guest's
 * quantities across a duration switch by remapping each line to its group
 * sibling. Variants without a duration are classic tickets and are never
 * filtered or remapped.
 *
 * All helpers are pure (no I/O); loaders and actions hold the variants
 * already.
 */

type TicketLine = { variantId: string; quantity: number };

/**
 * The variant fields these rules actually read. Generic over it so the same
 * helpers serve the server's full `TicketVariant` and the slimmer display shape
 * the experience page ships to the booking panel — the rules must not be
 * reimplemented for the client just because it carries less per variant.
 */
type DurationPriced = { durationSeconds: number | null };
type Grouped = DurationPriced & {
  id: string;
  groupKey: string | null;
  addOns?: readonly { id: string }[];
};

/**
 * The variants sellable at the selected duration: those sold for exactly that
 * duration plus duration-less (classic) variants. `null` — no duration
 * selected/offered — keeps the full list.
 */
export function filterVariantsForDuration<T extends DurationPriced>(
  variants: T[],
  durationSeconds: number | null
): T[] {
  if (durationSeconds === null) return variants;
  return variants.filter(
    (v) => v.durationSeconds === null || v.durationSeconds === durationSeconds
  );
}

/**
 * The duration whose variants a picker should show when the guest hasn't
 * chosen one yet (checkout step 1): the preferred duration (e.g. the basket's
 * stored slot) when some variant is sold at it, else the shortest sold
 * duration. `null` when no variant carries a duration — the experience
 * doesn't use per-duration tickets and no filtering applies.
 */
export function defaultVariantDurationSeconds(
  variants: readonly DurationPriced[],
  preferredSeconds?: number | null
): number | null {
  const durations = variants
    .map((v) => v.durationSeconds)
    .filter((d): d is number => d !== null);
  if (durations.length === 0) return null;
  if (preferredSeconds != null && durations.includes(preferredSeconds)) {
    return preferredSeconds;
  }
  return Math.min(...durations);
}

/**
 * Whether ANY offered duration sells something — the difference between "pick
 * another duration" and "this experience has nothing to sell", which a picker
 * built on these variants must say out loud rather than leave to a silently
 * disabled call-to-action. With no durations offered there is nothing to
 * filter by, so the plain catalogue answers.
 */
export function hasSellableVariants<T extends DurationPriced>(
  variants: T[],
  durationsSeconds: readonly number[]
): boolean {
  return durationsSeconds.length === 0
    ? variants.length > 0
    : durationsSeconds.some(
        (seconds) => filterVariantsForDuration(variants, seconds).length > 0
      );
}

/** Whether a variant is sold at `durationSeconds` as it stands — either sold for
 *  exactly that duration, or duration-less (a classic ticket). */
function passesThroughAt(
  variant: DurationPriced,
  durationSeconds: number
): boolean {
  return (
    variant.durationSeconds === null ||
    variant.durationSeconds === durationSeconds
  );
}

/** The same conceptual ticket sold at `durationSeconds` — the group sibling a
 *  quantity moves to when the duration changes. Ungrouped variants have none. */
function siblingAtDuration<T extends Grouped>(
  variant: T,
  variants: readonly T[],
  durationSeconds: number
): T | undefined {
  return variant.groupKey
    ? variants.find(
        (v) =>
          v.groupKey === variant.groupKey &&
          v.durationSeconds === durationSeconds
      )
    : undefined;
}

/**
 * The ticket lines a remap to `durationSeconds` would lose **and could have
 * kept**: the ones whose conceptual ticket simply isn't sold at that duration
 * (no same-duration variant, no group sibling). A caller that must not silently
 * discard a guest's choice — the basket write behind a duration switch — asks
 * this first and declines the switch instead.
 *
 * Two kinds of line are deliberately NOT reported, because no remap could keep
 * them and reporting them would make the answer always "something is lost":
 *
 * - **Add-on lines whose parent moves to a sibling.** A sibling's add-ons are
 *   different products with their own ids and prices, so an add-on cannot follow
 *   its parent across durations — the model has no link to follow. (An add-on
 *   whose *parent* is lost needs no separate report: the parent line is already
 *   in the answer.)
 * - **Zero-quantity and unknown-variant lines**, which carry no choice.
 */
export function ticketsLostToDuration<T extends Grouped>(
  tickets: readonly TicketLine[],
  variants: T[],
  durationSeconds: number | null
): TicketLine[] {
  if (durationSeconds === null) return [];
  const variantById = new Map(variants.map((v) => [v.id, v]));
  return tickets.filter((line) => {
    if (line.quantity <= 0) return false;
    const variant = variantById.get(line.variantId);
    // Add-on and unknown lines are judged through their parent, not on their own.
    if (!variant) return false;
    return (
      !passesThroughAt(variant, durationSeconds) &&
      siblingAtDuration(variant, variants, durationSeconds) === undefined
    );
  });
}

/**
 * Re-key a basket-style ticket list to the selected duration: lines already
 * at that duration (or duration-less) pass through, lines at another duration
 * move to the `groupKey` sibling sold at the selected one, and lines with no
 * such sibling are dropped — the ticket no longer exists at this duration, so
 * keeping it would charge for a ticket the picker can't show. Add-on lines
 * survive only while their parent variant passes through unchanged (a
 * sibling's add-ons are different products with their own ids and prices).
 * Zero-quantity and unknown-variant lines are dropped; lines that collide on
 * the same variant merge their quantities.
 */
export function remapTicketsForDuration<T extends Grouped>(
  tickets: readonly TicketLine[],
  variants: T[],
  durationSeconds: number | null
): TicketLine[] {
  if (durationSeconds === null) return [...tickets];

  const variantById = new Map(variants.map((v) => [v.id, v]));
  const parentByAddOnId = new Map(
    variants.flatMap((v) => (v.addOns ?? []).map((a) => [a.id, v] as const))
  );
  const passesThrough = (v: T) => passesThroughAt(v, durationSeconds);

  // Insertion-ordered so the output keeps the incoming line order.
  const quantities = new Map<string, number>();
  const add = (variantId: string, quantity: number) =>
    quantities.set(variantId, (quantities.get(variantId) ?? 0) + quantity);

  for (const line of tickets) {
    if (line.quantity <= 0) continue;

    const variant = variantById.get(line.variantId);
    if (variant) {
      if (passesThrough(variant)) {
        add(variant.id, line.quantity);
        continue;
      }
      const sibling = siblingAtDuration(variant, variants, durationSeconds);
      if (sibling) add(sibling.id, line.quantity);
      continue;
    }

    const parent = parentByAddOnId.get(line.variantId);
    if (parent && passesThrough(parent)) add(line.variantId, line.quantity);
  }

  return [...quantities].map(([variantId, quantity]) => ({
    variantId,
    quantity,
  }));
}
